import {
  AddressInput,
  Icons,
  Input,
  Label,
  MultiFileUploader,
  MultiSelect,
  PhoneInput,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SingleFileUploader,
  Textarea,
  Tooltip,
} from '@commenda/kakashi';
import { FileBasic } from '@commenda/types/src/file/types';
import { Address, SecureInformationRequestFieldType } from '@prisma/client';
import { useNotification } from '../../hooks/useNotification';
import { SecureInput } from './SecureInput';
import {
  GroupsOnChangeHandler,
  IStateSecureInformationRequestField,
} from './types';

export const SecureInformationRequestField = ({
  field,
  onChange,
  companyId,
}: {
  field: IStateSecureInformationRequestField;
  onChange: GroupsOnChangeHandler;
  companyId: number;
}) => {
  const { setNotification } = useNotification();

  return (
    <div className="flex flex-col gap-xs">
      <div className="flex items-center gap-xs">
        <div className="txt-small-medium text-text-secondary">
          {field.headerText}
        </div>
        {field.tooltipText ? (
          <Tooltip content={field.tooltipText}>
            <Icons.InfoCircle className="size-4 text-fg-tertiary" />
          </Tooltip>
        ) : null}
      </div>

      {field.type === SecureInformationRequestFieldType.TEXT &&
      field?.isSecure ? (
        <SecureInput field={field} onChange={onChange} type="text" />
      ) : field.type === SecureInformationRequestFieldType.TEXT &&
        !field?.isSecure ? (
        <Input
          type="text"
          value={field.value}
          placeholder="Enter required information"
          onChange={(e) => onChange(field.id, e.target.value)}
        />
      ) : null}

      {field.type === SecureInformationRequestFieldType.NUMBER &&
      field?.isSecure ? (
        <SecureInput field={field} onChange={onChange} type="number" />
      ) : field.type === SecureInformationRequestFieldType.NUMBER &&
        !field?.isSecure ? (
        <Input
          type="number"
          value={field.value}
          placeholder="Enter required information"
          onChange={(e) => onChange(field.id, e.target.value)}
        />
      ) : null}

      {field.type === SecureInformationRequestFieldType.BIG_TEXT ? (
        <Textarea
          value={field.value}
          placeholder="Enter required details"
          onChange={(e) => onChange(field.id, e.target.value)}
        />
      ) : null}

      {field.type === SecureInformationRequestFieldType.DATE ? (
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={field.value}
            placeholder="Enter required information"
            onChange={(e) => onChange(field.id, e.target.value)}
          />
        </div>
      ) : null}

      {field.type === SecureInformationRequestFieldType.RADIO ? (
        <div className="flex items-center gap-2">
          <RadioGroup
            className="gap-4"
            value={field.value}
            onValueChange={(value: 'true' | 'false') =>
              onChange(field.id, value)
            }
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="true" id="yes" />
              <Label htmlFor="yes" className="cursor-pointer">
                Yes
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="false" id="no" />
              <Label htmlFor="yes" className="cursor-pointer">
                No
              </Label>
            </div>
          </RadioGroup>
        </div>
      ) : null}

      {field.type === SecureInformationRequestFieldType.SELECT ? (
        <Select
          value={field.value}
          onValueChange={(val) => onChange(field.id, val)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select option" />
          </SelectTrigger>
          <SelectContent>
            {field.selectOptions.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}

      {field.type === SecureInformationRequestFieldType.MULTI_SELECT ? (
        <MultiSelect
          options={field.selectOptions.map((option) => ({
            value: option,
            label: option,
          }))}
          value={field.selectResponse || []}
          onValueChange={(val) => onChange(field.id, null, val)}
          defaultValue={field.selectResponse || []}
        />
      ) : null}

      {field.type === SecureInformationRequestFieldType.FILE ? (
        <SingleFileUploader
          companyId={companyId}
          file={field.files[0]}
          onFileChange={(file: FileBasic) => {
            onChange(field.id, null, null, [file]);
          }}
          setFile={(fl: FileBasic) => {
            onChange(field.id, null, null, [
              {
                fileName: fl.fileName,
                fileUrl: fl.fileUrl,
                documentType: fl.documentType,
                id: -1,
              },
            ]);
          }}
        />
      ) : null}

      {field.type === SecureInformationRequestFieldType.MULTI_FILE ? (
        <MultiFileUploader
          companyId={companyId}
          files={field.files}
          onFilesChange={(files: FileBasic[]) =>
            onChange(field.id, null, null, files)
          }
          onError={() => {
            setNotification({
              show: true,
              valid: false,
              title: 'Error',
              message:
                'Something went wrong please try again or contact Team Commenda if you need help',
            });
          }}
        />
      ) : null}

      {field.type === SecureInformationRequestFieldType.ADDRESS ? (
        <AddressInput
          address={field.address}
          onChange={(val: Address) => onChange(field.id, null, null, null, val)}
          hideLabels
        />
      ) : null}

      {field.type === SecureInformationRequestFieldType.PHONE_NUMBER ? (
        <PhoneInput
          value={field.value}
          onChange={(e) => onChange(field.id, e)}
          defaultCountry="US"
        />
      ) : null}
    </div>
  );
};
